import React, { useState } from 'react';
import GaugeRing from './GaugeRing';
import CashflowMini, { type CashflowMiniRow } from './CashflowMini';

export type ProgressCashflowModel = {
    progress: { actual: number; target?: number };
    collection: { actual: number; target?: number };
    spending: { actual: number; target?: number };
    title?: string;
    subtitle?: string;
};

export default function ProgressOverview({
    model,
    cashflowMini,
    onOpenPayments,
}: {
    model: ProgressCashflowModel;
    cashflowMini?: { data: CashflowMiniRow[]; currency?: string | null };
    onOpenPayments?: () => void;
}) {
    const [cashflowCollapsed, setCashflowCollapsed] = useState(false);

    return (
        <div className="rounded-[24px] border border-slate-200/80 bg-white/96 p-5 shadow-[0_16px_40px_-26px_rgba(15,23,42,0.26)]">
            <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
                <div>
                    <div className="text-base font-semibold tracking-tight text-slate-900">
                        {model.title ?? '공정 · 현금흐름 스냅샷'}
                    </div>
                    {model.subtitle && <div className="mt-1 text-sm text-slate-500">{model.subtitle}</div>}
                </div>
                <div className="mt-2 text-xs text-slate-500 md:mt-0">계획 대비 현재 위치를 확인하세요</div>
            </div>

            <div className="mt-4 grid grid-cols-1 items-stretch gap-3 md:grid-cols-2 2xl:grid-cols-4">
                <GaugeRing
                    title="공정 달성"
                    subtitle="계획 공정 vs 실제 공정"
                    actual={model.progress.actual}
                    target={model.progress.target}
                    variant="PROGRESS"
                />
                <GaugeRing
                    title="대금 회수"
                    subtitle="계획 회수 vs 실제 회수"
                    actual={model.collection.actual}
                    target={model.collection.target}
                    variant="COLLECTION"
                />
                <GaugeRing
                    title="비용 집행"
                    subtitle="계획 집행 vs 실제 집행"
                    actual={model.spending.actual}
                    target={model.spending.target}
                    variant="SPENDING"
                />

                <div>
                    <CashflowMini
                        data={cashflowMini?.data ?? []}
                        currency={cashflowMini?.currency ?? null}
                        collapsed={cashflowCollapsed}
                        onToggleCollapsed={() => setCashflowCollapsed((v) => !v)}
                        onOpenDetail={onOpenPayments}
                        height={140}
                    />
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-3 text-sm text-slate-600">
                <span className="font-medium text-slate-800">힌트</span>
                <span className="ml-2">
                    “비용 집행”이 “공정 달성”보다 빠르거나, “대금 회수”가 공정보다 낮으면 현금흐름 점검이 필요합니다.
                </span>
            </div>
        </div>
    );
}
